// src/pages/ConnectBigCommerce.jsx
import React, {useEffect, useState, useContext} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {Spinner, Card, Text, Banner, BlockStack, Button} from '@shopify/polaris';
import {AuthContext} from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL;

export default function ConnectBigCommerce() {
  const navigate = useNavigate();
  const {search} = useLocation();
  const params = new URLSearchParams(search);
  const site_id    = params.get('site_id');
  const site_token = params.get('site_token');
  const platform   = params.get('platform'); // 'BigCommerce'
  const store_hash = params.get('store_hash');

  const [error, setError] = useState(null);
  const [siteLimit, setSiteLimit] = useState(false);       // one-site-per-user
  const [alreadyClaimed, setAlreadyClaimed] = useState(false); // store claimed (generic)
  const { token } = useContext(AuthContext);

  useEffect(() => {
    if (!site_id || !site_token || !store_hash) {
      setError('Missing BigCommerce parameters. Cannot proceed.');
      return;
    }

    (async () => {
      try {
        // Step 1: claim
        const claimRes = await fetch(`${API}/api/sites/claim`, {
          method:  'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ site_id: Number(site_id), site_token }),
        });

        if (claimRes.status === 409) {
          // Try reading detail to differentiate one-site-per-account vs claimed-by-someone
          let body = {};
          try { body = await claimRes.json(); } catch {}

          const detail = (body?.detail || '').toLowerCase();

          // One-site-per-account
          if (detail.includes('only connect one site') ||
              detail.includes('only register one site') ||
              detail.includes('one site per account')) {
            setSiteLimit(true);
            setError(
              body?.detail ||
              'You may only connect one site per account. If you want to replace the site connected to your account you can do this in Settings.'
            );
            return;
          }

          // Generic "already claimed" flow (your requested message)
          setAlreadyClaimed(true);
          return;
        }

        if (!claimRes.ok) {
          const body = await claimRes.json().catch(() => ({}));
          throw new Error(body.detail || claimRes.statusText || 'Failed to claim site.');
        }

        // Step 2: inject scripts
        const res = await fetch(`${API}/bigcommerce/inject`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ site_id: Number(site_id) }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || res.statusText || 'Failed to inject BigCommerce scripts.');
        }

        // Step 3: go to integrations
        navigate(
          `/integrations/bigcommerce` +
          `?site_id=${encodeURIComponent(site_id)}` +
          `&site_token=${encodeURIComponent(site_token)}` +
          `&store_hash=${encodeURIComponent(store_hash)}` +
          `&platform=${encodeURIComponent(platform || 'BigCommerce')}`
        );
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [site_id, site_token, store_hash, token, navigate, platform]);

  // One-site-per-account banner
  if (siteLimit) {
    return (
      <Card sectioned>
        <Banner status="warning" title="You already have a site connected">
          <p>
            {error ||
              'You may only connect one site per account. If you want to replace the site connected to your account you can do this in Settings.'}
          </p>
          <BlockStack gap="200">
            <Button primary onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
            <Button onClick={() => navigate('/settings')}>Go to Settings</Button>
          </BlockStack>
        </Banner>
      </Card>
    );
  }

  // Store already claimed — show your requested message + buttons
  if (alreadyClaimed) {
    return (
      <Card sectioned>
        <Banner status="critical" title="This store has already been claimed">
          <p>
            If this store was claimed by you, go to your dashboard. If you believe this is an error, contact support.
          </p>
          <BlockStack gap="200">
            <Button primary onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
            <Button onClick={() => navigate('/ContactUs')}>Contact Support</Button>
          </BlockStack>
        </Banner>
      </Card>
    );
  }

  // Generic error
  if (error) {
    return <Banner status="critical">{error}</Banner>;
  }

  // Loading
  return (
    <Card sectioned>
      <Spinner size="large" accessibilityLabel="Injecting scripts…" />
      <Text as="p">Linking your BigCommerce store and injecting scripts, please wait…</Text>
    </Card>
  );
}
